<template>
  <div style="width: 100%; height: 100%">
    <p v-if="error" class="mb-4" style="color: red; font-weight: bold">
      {{ error }}
    </p>
    <div
      ref="cameraWrapper"
      class="round-camera"
      :class="{ 'is-mirrored': isFrontCamera }"
      :style="{
        position: 'relative',
        width: '100%',
        height: error ? 'calc(100% - 42px)' : 'calc(100% - 4px)',
      }"
    >
      <div
        v-if="isFirstCameraReady"
        class="d-flex align-center justify-center"
        style="
          width: 100%;
          height: 100%;
          position: absolute;
          background-color: #eeeeee;
          border-radius: 24px;
          z-index: 1;
        "
      >
        <v-icon :color="site.color">mdi-camera-outline</v-icon>
        <v-progress-circular
          indeterminate
          size="50"
          :color="site.color"
          style="position: absolute"
        >
        </v-progress-circular>
      </div>
      <div
        v-if="isCameraReady"
        style="position: relative; width: 100%; height: 100%"
      >
        <video
          ref="video"
          class="camera-video"
          autoplay
          playsinline
          muted
        ></video>
        <canvas ref="canvas" class="camera-canvas"></canvas>

        <div class="scan-center">
          <img
            src="@/assets/images/scan.svg"
            alt="scan-qr-code"
            style="width: 100%; height: 100%; position: absolute"
          />

          <v-chip small style="position: absolute; bottom: 5%">
            {{ $t("detailScanQrCode") }}
          </v-chip>
        </div>
        <div v-show="isLoading" class="scan-confirmation">
          <v-row class="d-flex align-center justify-center">
            <v-progress-circular indeterminate size="50" :color="site.color">
            </v-progress-circular>
          </v-row>
        </div>
      </div>
      <div
        v-else
        class="d-flex align-center justify-center"
        style="
          width: 100%;
          height: 100%;
          background-color: #eeeeee;
          border-radius: 24px;
        "
      >
        <v-icon>mdi-camera-off-outline</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import jsQR from "jsqr";
import { mapState } from "vuex";

export default {
  emits: ["onDecode"],
  props: {
    isReady: { default: false },
    isLoading: { default: false },
  },
  data() {
    return {
      error: "",
      isFirstCameraReady: true,
      isCameraReady: false,
      isFrontCamera: false,

      stream: null,
      animationFrameId: null,
      isScanning: false,
      lastResult: null,
      selectDeviceId: null,
    };
  },
  mounted() {},
  beforeDestroy() {
    this.stopCamera();
  },
  methods: {
    // ========== ========== Init ========== ==========
    checkCameraFacingMode() {
      this.$nextTick(() => {
        const wrapperEl = this.$refs.cameraWrapper;
        const videoEl = wrapperEl ? wrapperEl.querySelector("video") : null;

        if (!videoEl || !videoEl.srcObject) {
          this.isFrontCamera = true;
          return;
        }

        const videoTrack = videoEl.srcObject.getVideoTracks()[0];
        if (!videoTrack) {
          this.isFrontCamera = true;
          return;
        }

        // 1. ลองดู capabilities ก่อน
        if (videoTrack.getCapabilities) {
          const capabilities = videoTrack.getCapabilities();
          // console.log("Camera capabilities:", capabilities);
          if (
            capabilities &&
            Array.isArray(capabilities.facingMode) &&
            capabilities.facingMode.length > 0
          ) {
            this.isFrontCamera = capabilities.facingMode.includes("user");
            return;
          }
        }

        // 2. fallback ไปดู settings
        if (videoTrack.getSettings) {
          const settings = videoTrack.getSettings();
          // console.log("Camera settings:", settings);
          if (settings.facingMode) {
            this.isFrontCamera = settings.facingMode === "user";
            return;
          }
        }

        // 3. browser ไม่บอกอะไรเลย
        this.isFrontCamera = true;
      });
    },
    async getCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        const cameras = devices.filter((device) => {
          return device.kind === "videoinput";
        });
        if (cameras.length === 0) {
          return [];
        }

        const rearKeywords = [
          "back",
          "rear",
          "environment",
          "กล้องหลัง",
          "ด้านหลัง",
          "หลัง",
        ];
        const frontKeywords = [
          "front",
          "user",
          "กล้องหน้า",
          "ด้านหน้า",
          "หน้า",
        ];
        const zoomKeywords = [
          // Ultra wide
          "ultra",
          "ultrawide",
          "ultra wide",
          "ultra-wide",
          "ultra wide angle",

          // Telephoto / Zoom
          "tele",
          "telephoto",
          "tele photo",
          "tele-phot",
          "periscope",
          "periscope telephoto",
          "zoom",

          // ระบุระดับ zoom
          "2x",
          "3x",
          "5x",
          "10x",
          "2 x",
          "3 x",
          "5 x",
          "10 x",

          // Macro
          "macro",

          // ภาษาไทย
          "อัลตร้าไวด์",
          "อัลตร้าไวด์",
          "อัลตร้าไวด์",
          "เทเล",
          "เทเลโฟโต้",
          "เทเลโฟโต",
          "เพอริสโคป",
          "ซูม",
          "มาโคร",
        ];

        const getLabel = (camera) => {
          return (camera.label || "").toLowerCase();
        };

        const isRearCamera = (camera) => {
          const label = getLabel(camera);

          return rearKeywords.some((keyword) => {
            return label.includes(keyword);
          });
        };

        const isFrontCamera = (camera) => {
          const label = getLabel(camera);

          return frontKeywords.some((keyword) => {
            return label.includes(keyword);
          });
        };

        const isZoomCamera = (camera) => {
          const label = getLabel(camera);

          return zoomKeywords.some((keyword) => {
            return label.includes(keyword);
          });
        };

        const rearCameras = cameras.filter((camera) => {
          return isRearCamera(camera);
        });

        const normalRearCameras = rearCameras.filter((camera) => {
          return !isZoomCamera(camera);
        });

        const frontCameras = cameras.filter((camera) => {
          return isFrontCamera(camera);
        });

        /*
         * 1. มีกล้องหลังที่ไม่ใช่
         *    ultra / tele / zoom / macro
         */
        if (normalRearCameras.length > 0) {
          console.log("Normal rear cameras:", normalRearCameras);
          return normalRearCameras;
        }

        /*
         * 2. มีกล้องหลัง แต่ label แยกไม่ได้
         */
        if (rearCameras.length > 0) {
          console.log("Rear cameras:", rearCameras);
          return [rearCameras[0]];
        }

        /*
         * 3. ไม่มีกล้องหลัง
         *    ใช้กล้องหน้า
         */
        if (frontCameras.length > 0) {
          console.log("Front camera:", frontCameras[0]);
          return [frontCameras[0]];
        }

        /*
         * 4. fallback
         */
        console.log("Cannot determine camera:", cameras[0]);
        return [cameras[0]];
      } catch (error) {
        console.error("Cannot enumerate cameras:", error);
        return [];
      }
    },
    async selectCamera(deviceId) {
      console.log("Selecting camera:", deviceId);
      this.stopScanning();
      this.$nextTick(() => {
        this.startCamera(deviceId);
      });
      this.selectDeviceId = deviceId;
    },
    async startCamera(deviceId = null) {
      try {
        const video = this.$refs.video;
        if (!video) {
          console.error("Video element not found");
          return;
        }

        if (this.stream) {
          this.stream.getTracks().forEach((track) => {
            track.stop();
          });
          this.stream = null;
        }

        const videoConstraints = {
          width: {
            ideal: 1920,
          },
          height: {
            ideal: 1080,
          },
        };

        if (deviceId) {
          videoConstraints.deviceId = {
            exact: deviceId,
          };
        } else {
          videoConstraints.facingMode = "environment";
        }

        this.stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        video.srcObject = this.stream;
        await video.play();

        const track = this.stream.getVideoTracks()[0];
        if (track) {
          // console.log("Camera track:", track);
          if (track.getSettings) {
            // console.log("Camera settings:", track.getSettings());
          }
          if (track.getCapabilities) {
            // console.log("Camera capabilities:", track.getCapabilities());
          }
        }

        this.checkCameraFacingMode();

        const cameras = await this.getCameras();
        if (
          this.isFirstCameraReady &&
          cameras.length > 0 &&
          !this.selectDeviceId
        ) {
          this.isFirstCameraReady = false;
          this.selectCamera(cameras[0].deviceId);
          return;
        }
        this.startScanning();
      } catch (error) {
        console.log("error :>> ", error);
        this.isFrontCamera = false;
        this.isCameraReady = false;
        this.error = this.$t("cameraError.unknown", {
          error_name: error.name,
        });
      }
    },

    // ========== ========== Scan ========== ==========
    startScanning() {
      if (this.isScanning) {
        return;
      }

      this.isScanning = true;
      this.scanFrame();
    },
    scanFrame() {
      if (!this.isScanning) {
        return;
      }

      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        this.animationFrameId = requestAnimationFrame(() => {
          this.scanFrame();
        });

        return;
      }

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) {
        this.animationFrameId = requestAnimationFrame(() => {
          this.scanFrame();
        });

        return;
      }

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      const context = canvas.getContext("2d", {
        willReadFrequently: true,
      });
      context.drawImage(video, 0, 0, width, height);
      const imageData = context.getImageData(0, 0, width, height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      if (code) {
        this.onDecode(code.data);
      }

      // สำคัญ: ไม่ว่าจะเจอ QR หรือไม่
      // ต้อง schedule frame ถัดไปเสมอ
      this.animationFrameId = requestAnimationFrame(() => {
        this.scanFrame();
      });
    },

    // ========== ========== Stop ========== ==========
    stopScanning() {
      this.isScanning = false;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    async stopCamera() {
      this.stopScanning();

      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          track.stop();
        });
        this.stream = null;
      }

      const video = this.$refs.video;
      if (video) {
        video.pause();
        video.srcObject = null;
      }

      this.isFrontCamera = false;
      this.isCameraReady = false;
    },

    onDecode(value) {
      if (value === this.lastResult) {
        return;
      }
      this.lastResult = value;
      console.log("QR Code:", value);
      this.$emit("onDecode", value);
    },
  },
  computed: {
    ...mapState("defaultSystem", ["site"]),
  },
  filters: {},
  watch: {
    isReady: {
      handler() {
        this.isCameraReady = this.isReady;
        if (this.isCameraReady) {
          this.$nextTick(() => {
            this.startCamera(this.selectDeviceId);
          });
        } else {
          this.stopCamera();
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.is-mirrored :deep(video) {
  transform: scaleX(-1);
}
.round-camera :deep(video) {
  border-radius: 24px !important;
}

.camera-wrapper {
  position: relative;
  width: 100%;
  height: 60vh;
  overflow: hidden;
  border-radius: 24px;
  background: #000;
}
.camera-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.camera-canvas {
  display: none;
}

.scan-confirmation {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
}
.scan-center {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
</style>
